window.MaisonBundleCart =
  window.MaisonBundleCart ||
  (() => {
    const DISCOUNT_PROPERTY = "_pdp_bundle_widget_discount";
    const NONCE_PROPERTY = "_pdp_bundle_widget_nonce";
    const GIFT_PROPERTY = "_pdp_bundle_widget_gift";
    const GIFT_QUANTITY_PROPERTY = "_pdp_bundle_widget_gift_quantity";

    function getCountFromDiscount(discount) {
      const match = String(discount || "").trim().match(/^tier-(\d+)$/);
      if (!match) return 0;

      const count = parseInt(match[1], 10);
      return Number.isNaN(count) || count < 1 ? 0 : count;
    }

    function getPropertyValue(properties, propertyName) {
      if (!properties) return "";

      if (Array.isArray(properties)) {
        const matchingProperty = properties.find((property) => {
          if (Array.isArray(property)) return property[0] === propertyName;
          return (
            property &&
            (property.name === propertyName || property.key === propertyName)
          );
        });

        if (!matchingProperty) return "";
        if (Array.isArray(matchingProperty)) return matchingProperty[1];

        return matchingProperty.value;
      }

      return properties[propertyName];
    }

    function getCleanProperty(item, propertyName) {
      return String(getPropertyValue(item.properties, propertyName) || "").trim();
    }

    function isGiftLine(item) {
      return getCleanProperty(item, GIFT_PROPERTY) === "true";
    }

    function getExpectedGiftQuantity(item) {
      const quantity = parseInt(getCleanProperty(item, GIFT_QUANTITY_PROPERTY), 10);
      return Number.isNaN(quantity) || quantity < 1 ? 1 : quantity;
    }

    function hasExpectedGiftQuantity(line) {
      const quantity = parseInt(line.item.quantity, 10);
      if (Number.isNaN(quantity) || quantity < 1) return false;

      return quantity === getExpectedGiftQuantity(line.item);
    }

    function getBundleGroups(cart) {
      const groups = new Map();

      (cart.items || []).forEach((item, index) => {
        const nonce = getCleanProperty(item, NONCE_PROPERTY);
        if (!nonce) return;

        if (!groups.has(nonce)) {
          groups.set(nonce, {
            nonce,
            lines: [],
            paidLines: [],
            giftLines: [],
          });
        }

        const line = {
          item,
          line: index + 1,
          key: item.key,
          isGift: isGiftLine(item),
        };
        const group = groups.get(nonce);

        group.lines.push(line);
        if (line.isGift) group.giftLines.push(line);
        else group.paidLines.push(line);
      });

      return Array.from(groups.values());
    }

    function getExpectedDiscount(group) {
      const discounts = group.paidLines
        .map((line) => getCleanProperty(line.item, DISCOUNT_PROPERTY))
        .filter(Boolean);

      if (!discounts.length) return "";

      const expectedDiscount = discounts[0];
      return discounts.every((discount) => discount === expectedDiscount) ? expectedDiscount : "";
    }

    function getExpectedCount(group) {
      return getCountFromDiscount(getExpectedDiscount(group));
    }

    function isCompleteBundleGroup(group) {
      const expectedDiscount = getExpectedDiscount(group);
      const expectedCount = getExpectedCount(group);
      const paidQuantity = group.paidLines.reduce((sum, line) => {
        const quantity = parseInt(line.item.quantity, 10);
        return sum + (Number.isNaN(quantity) ? 0 : quantity);
      }, 0);

      if (!expectedDiscount) return false;
      if (!expectedCount || paidQuantity === 0 || paidQuantity % expectedCount !== 0) return false;

      return (
        group.paidLines.every((line) => {
          return getCleanProperty(line.item, DISCOUNT_PROPERTY) === expectedDiscount;
        }) && group.giftLines.every(hasExpectedGiftQuantity)
      );
    }

    function hasEligibleCompleteBundle(cart) {
      return getBundleGroups(cart).some((group) => {
        if (!isCompleteBundleGroup(group)) return false;

        const discount = getCleanProperty(group.paidLines[0].item, DISCOUNT_PROPERTY);
        return getCountFromDiscount(discount) >= 3;
      });
    }

    function getInvalidBundleKeys(cart) {
      return getBundleGroups(cart)
        .filter((group) => !isCompleteBundleGroup(group))
        .flatMap((group) => group.lines.map((line) => line.key).filter(Boolean));
    }

    async function fetchCart() {
      const response = await fetch(`${routes.cart_url}.js`, {
        credentials: "same-origin",
        headers: { Accept: "application/json" },
      });

      if (!response.ok) throw new Error("Unable to load cart");

      return response.json();
    }

    async function removeKeys(keys, sections = []) {
      const uniqueKeys = [...new Set(keys)].filter(Boolean);
      const body = {
        updates: uniqueKeys.reduce((updates, key) => {
          updates[key] = 0;
          return updates;
        }, {}),
      };

      if (sections.length) {
        body.sections = sections.map((section) => section.section || section.id);
        body.sections_url = window.location.pathname;
      }

      const response = await fetch(`${routes.cart_update_url}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) throw new Error("Unable to update cart");

      return response.json();
    }

    async function removeBundleGroup(nonce, sections = []) {
      const cart = await fetchCart();
      const group = getBundleGroups(cart).find(
        (itemGroup) => itemGroup.nonce === nonce
      );

      if (!group) return cart;

      return removeKeys(
        group.lines.map((line) => line.key),
        sections
      );
    }

    async function scaleBundleGroup(nonce, newQuantity, sections = []) {
      const cart = await fetchCart();
      const group = getBundleGroups(cart).find(
        (itemGroup) => itemGroup.nonce === nonce
      );

      if (!group) return cart;

      const body = {
        updates: group.paidLines.reduce((updates, line) => {
          if (line.key) updates[line.key] = newQuantity;
          return updates;
        }, {}),
      };

      if (sections.length) {
        body.sections = sections.map((section) => section.section || section.id);
        body.sections_url = window.location.pathname;
      }

      const response = await fetch(`${routes.cart_update_url}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) throw new Error("Unable to scale bundle group");

      return response.json();
    }

    async function cleanupInvalidBundles(sections = []) {
      const cart = await fetchCart();
      const invalidKeys = getInvalidBundleKeys(cart);

      if (!invalidKeys.length) {
        return { cart, cleaned: false };
      }

      return {
        cart: await removeKeys(invalidKeys, sections),
        cleaned: true,
      };
    }

    return {
      cleanupInvalidBundles,
      getBundleGroups,
      getPropertyValue,
      hasEligibleCompleteBundle,
      isCompleteBundleGroup,
      removeBundleGroup,
      scaleBundleGroup,
    };
  })();

class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener("click", (event) => {
      event.preventDefault();
      const cartItems =
        this.closest("cart-items") || this.closest("cart-drawer-items");
      cartItems.updateQuantity(this.dataset.index, 0);
    });
  }
}

customElements.define("cart-remove-button", CartRemoveButton);

class CartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement =
      document.getElementById("shopping-cart-line-item-status") ||
      document.getElementById("CartDrawer-LineItemStatus");

    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, ON_CHANGE_DEBOUNCE_TIMER);

    this.addEventListener("change", debouncedOnChange.bind(this));

    // Use event delegation to handle upsell toggle events
    // This prevents multiple event listeners when DOM elements are replaced
    if (!CartItems.upsellToggleListenerAttached) {
      document.addEventListener("change", (event) => {
        if (event.target && event.target.id === "cart-upsell-toggle") {
          // Find the active cart-items instance to handle the event
          const activeCartItems = document.querySelector("cart-items") || document.querySelector("cart-drawer-items");
          if (activeCartItems && activeCartItems.onCartUpsellToggle) {
            activeCartItems.onCartUpsellToggle(event);
          }
        }
      });
      
      // Listen for cart open events to update toggle state
      document.addEventListener("cart:open", () => {
        const activeCartItems = document.querySelector("cart-items") || document.querySelector("cart-drawer-items");
        if (activeCartItems && activeCartItems.updateCartUpsellToggleState) {
          activeCartItems.updateCartUpsellToggleState().catch(e => console.error(e));
        }
      });
      
      // Listen for cart updated events to update toggle state
      document.addEventListener("cart:updated", () => {
        const activeCartItems = document.querySelector("cart-items") || document.querySelector("cart-drawer-items");
        if (activeCartItems && activeCartItems.updateCartUpsellToggleState) {
          activeCartItems.updateCartUpsellToggleState().catch(e => console.error(e));
        }
      });
      
      CartItems.upsellToggleListenerAttached = true;
    }
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(
      PUB_SUB_EVENTS.cartUpdate,
      (event) => {
        if (event.source === "cart-items") {
          return;
        }
        this.onCartUpdate();
      }
    );
    if (this.tagName !== "CART-DRAWER-ITEMS") {
      fetch(`${routes.cart_url}.js`)
        .then((response) => response.json())
        .then((parsedState) => {
          this.updateCartUpsellToggleState().catch(e => console.error(e));
          this.updateCartUpsellVisibility(parsedState.item_count);
        })
        .catch((e) => {
          console.error(e);
        });
    } else {
      // For cart-drawer-items, also update toggle state on initial load
      setTimeout(() => {
        this.updateCartUpsellToggleState().catch(e => console.error(e));
      }, 100); // Small delay to ensure DOM is ready
    }
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute("value");
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;
    let message = "";

    if (inputValue < event.target.dataset.min) {
      message = window.quickOrderListStrings.min_error.replace(
        "[min]",
        event.target.dataset.min
      );
    } else if (inputValue > parseInt(event.target.max)) {
      message = window.quickOrderListStrings.max_error.replace(
        "[max]",
        event.target.max
      );
    } else if (inputValue % parseInt(event.target.step) !== 0) {
      message = window.quickOrderListStrings.step_error.replace(
        "[step]",
        event.target.step
      );
    }

    if (message) {
      this.setValidity(event, index, message);
    } else {
      event.target.setCustomValidity("");
      event.target.reportValidity();
      this.updateQuantity(
        index,
        inputValue,
        document.activeElement.getAttribute("name"),
        event.target.dataset.quantityVariantId
      );
    }
  }

  onChange(event) {
    this.validateQuantity(event);
  }

  getCartLineElements(line) {
    const isCartDrawer = this.tagName === "CART-DRAWER-ITEMS";
    const lineItemSelectors = isCartDrawer
      ? [`#CartDrawer-Item-${line}`, `#CartItem-${line}`]
      : [`#CartItem-${line}`, `#CartDrawer-Item-${line}`];
    const quantitySelectors = isCartDrawer
      ? [`#Drawer-quantity-${line}`, `#Quantity-${line}`]
      : [`#Quantity-${line}`, `#Drawer-quantity-${line}`];
    const scopedRoot = this.isConnected ? this : null;
    const findElement = (root, selectors) => {
      if (!root) return null;

      for (const selector of selectors) {
        const element = root.querySelector(selector);
        if (element) return element;
      }

      return null;
    };

    return {
      lineItem:
        findElement(scopedRoot, lineItemSelectors) ||
        findElement(document, lineItemSelectors),
      quantityElement:
        findElement(scopedRoot, quantitySelectors) ||
        findElement(document, quantitySelectors),
    };
  }

  getBundleLineData(line) {
    const { lineItem, quantityElement } = this.getCartLineElements(line);
    const source = quantityElement || lineItem;
    const nonce = source ? source.dataset.bundleNonce : "";

    return {
      nonce,
      isGift: source ? source.dataset.bundleGift === "true" : false,
      quantityElement,
    };
  }

  resetBundleQuantity(line) {
    const { quantityElement } = this.getCartLineElements(line);

    if (quantityElement) {
      quantityElement.value = quantityElement.getAttribute("value") || "1";
    }
  }

  onCartUpdate() {
    if (this.tagName === "CART-DRAWER-ITEMS") {
      fetch(`${routes.cart_url}?section_id=cart-drawer`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(
            responseText,
            "text/html"
          );
          const cartDrawer = document.querySelector("cart-drawer");
          const sourceCartDrawer = html.querySelector("cart-drawer");
          const currentB2bCartMode =
            cartDrawer && cartDrawer.dataset.b2bCartMode === "true";
          const sourceB2bCartMode =
            sourceCartDrawer && sourceCartDrawer.dataset.b2bCartMode === "true";
          const currentIsEmpty =
            cartDrawer && cartDrawer.classList.contains("is-empty");
          const sourceIsEmpty =
            sourceCartDrawer && sourceCartDrawer.classList.contains("is-empty");

          if (
            cartDrawer &&
            (currentB2bCartMode ||
              sourceB2bCartMode ||
              currentIsEmpty !== sourceIsEmpty)
          ) {
            const targetElement = document.querySelector("#CartDrawer .drawer__inner");
            const sourceElement = html.querySelector("#CartDrawer .drawer__inner");
            if (targetElement && sourceElement) {
              targetElement.innerHTML = sourceElement.innerHTML;
            }
          } else {
            const selectors = ["cart-drawer-items", ".cart-drawer__footer"];
            for (const selector of selectors) {
              const targetElement = document.querySelector(selector);
              const sourceElement = html.querySelector(selector);
              if (targetElement && sourceElement) {
                targetElement.replaceWith(sourceElement);
              }
            }
          }
          if (cartDrawer && sourceCartDrawer) {
            cartDrawer.dataset.b2bCartMode =
              sourceB2bCartMode ? "true" : "false";
            cartDrawer.classList.toggle("is-empty", sourceIsEmpty);
            this.updateCartUpsellVisibility(sourceIsEmpty ? 0 : 1);

            const targetItemCount = cartDrawer.querySelector(
              ".cart-drawer__item-count"
            );
            const sourceItemCount = sourceCartDrawer.querySelector(
              ".cart-drawer__item-count"
            );
            if (targetItemCount && sourceItemCount) {
              targetItemCount.textContent = sourceItemCount.textContent;
            }
          }
          this.updateCartUpsellToggleState().catch(e => console.error(e));
        })
        .catch((e) => {
          console.error(e);
        });
    } else {
      fetch(`${routes.cart_url}?section_id=main-cart-items`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(
            responseText,
            "text/html"
          );
          const sourceQty = html.querySelector("cart-items");
          if (!sourceQty) return;

          this.innerHTML = sourceQty.innerHTML;
          const sourceIsEmpty = sourceQty.classList.contains("is-empty");
          this.classList.toggle("is-empty", sourceIsEmpty);
          this.updateCartUpsellToggleState().catch(e => console.error(e));
          this.updateCartUpsellVisibility(sourceIsEmpty ? 0 : 1);
        })
        .catch((e) => {
          console.error(e);
        });
    }
  }

  async updateCartUpsellToggleState() {
    const cartUpsellToggle = document.getElementById("cart-upsell-toggle");
    const scriptTag = document.querySelector(
      "script[data-cart-upsell-variant-id]"
    );
    const cartUpsellVariantId = scriptTag
      ? scriptTag.dataset.cartUpsellVariantId
      : "";

    if (!cartUpsellVariantId || !cartUpsellToggle) {
      return;
    }

    // Don't update toggle state if we're in the middle of an operation
    if (this.addingUpsellProduct || this.removingUpsellProduct) {
      return;
    }

    try {
      // Get the current cart to check for upsell items
      const cartResponse = await fetch(`${routes.cart_url}.js`);
      const cart = await cartResponse.json();
      
      // Find the upsell item by variant ID and properties
      const upsellItem = cart.items.find(item => {
        // First check if variant ID matches
        if (item.variant_id.toString() === cartUpsellVariantId.toString()) {
          // If it has the special properties, use them for identification
          if (item.properties && item.properties._upsell === 'true' && item.properties._upsell_type === 'cart_checkbox') {
            return true;
          }
          // If no properties or no upsell properties, assume it's the upsell product
          if (!item.properties || !item.properties._upsell) {
            return true;
          }
        }
        return false;
      });

      const shouldBeChecked = !!upsellItem;
      
      if (cartUpsellToggle.checked !== shouldBeChecked) {
        cartUpsellToggle.checked = shouldBeChecked;
      }
    } catch (error) {
      console.error("Error checking upsell toggle state:", error);
    }
  }

  updateCartUpsellVisibility(itemCount) {
    const cartUpsellContainer = document.querySelector(
      ".cart-upsell-toggle-container"
    );
    if (cartUpsellContainer) {
      if (itemCount === 0) {
        cartUpsellContainer.classList.add("hidden");
      } else {
        cartUpsellContainer.classList.remove("hidden");
      }
    }
  }

  onCartUpsellToggle(event) {
    const scriptTag = document.querySelector(
      "script[data-cart-upsell-variant-id]"
    );
    const cartUpsellVariantId = scriptTag
      ? scriptTag.dataset.cartUpsellVariantId
      : "";
    const isChecked = event.target.checked;

    if (!cartUpsellVariantId) {
      console.error("No upsell variant ID found. Cannot proceed with upsell operation.");
      event.target.checked = false; // Reset the toggle
      return;
    }

    if (isChecked) {
      if (!this.addingUpsellProduct) {
        this.addingUpsellProduct = true;
        event.target.disabled = true; // Disable toggle during operation
        
        // Set a timeout to reset the flag in case something goes wrong
        const timeoutId = setTimeout(() => {
          console.warn("Upsell addition timeout - resetting flags");
          this.addingUpsellProduct = false;
          event.target.disabled = false;
        }, 10000); // 10 second timeout
        
        this.addUpsellProduct(cartUpsellVariantId).then(() => {
          clearTimeout(timeoutId);
          // Fetch cart data to update progress bar
          fetch(`${routes.cart_url}.js`)
            .then((response) => response.json())
            .then((parsedState) => {
              this.updateProgressBar({
                total_price: parsedState.total_price,
                items_subtotal_price: parsedState.items_subtotal_price,
              });
            })
            .catch((e) => console.error(e))
            .finally(() => {
              this.addingUpsellProduct = false;
              event.target.disabled = false; // Re-enable toggle
            });
        }).catch((error) => {
          clearTimeout(timeoutId);
          console.error("Failed to add upsell product:", error);
          event.target.checked = false; // Reset the toggle on error
          this.addingUpsellProduct = false;
          event.target.disabled = false; // Re-enable toggle
        });
      } else {
        event.target.checked = false; // Reset toggle since we can't proceed
      }
    } else {
      if (!this.removingUpsellProduct) {
        this.removingUpsellProduct = true;
        event.target.disabled = true; // Disable toggle during operation
        
        // Set a timeout to reset the flag in case something goes wrong
        const timeoutId = setTimeout(() => {
          console.warn("Upsell removal timeout - resetting flags");
          this.removingUpsellProduct = false;
          event.target.disabled = false;
        }, 10000); // 10 second timeout
        
        this.removeUpsellProduct(cartUpsellVariantId).then(() => {
          clearTimeout(timeoutId);
          // Fetch cart data to update progress bar
          fetch(`${routes.cart_url}.js`)
            .then((response) => response.json())
            .then((parsedState) => {
              this.updateProgressBar({
                total_price: parsedState.total_price,
                items_subtotal_price: parsedState.items_subtotal_price,
              });
            })
            .catch((e) => console.error(e))
            .finally(() => {
              this.removingUpsellProduct = false;
              event.target.disabled = false; // Re-enable toggle
            });
        }).catch((error) => {
          clearTimeout(timeoutId);
          console.error("Failed to remove upsell product:", error);
          this.removingUpsellProduct = false;
          event.target.checked = true; // Reset the toggle on error
          event.target.disabled = false; // Re-enable toggle
        });
      } else {
        event.target.checked = true; // Reset toggle since we can't proceed
      }
    }
  }

  async addUpsellProduct(cartUpsellVariantId) {
    try {
      // First, check if the upsell product is already in the cart
      const cartResponse = await fetch(`${routes.cart_url}.js`);
      const cart = await cartResponse.json();
      
      // Check if upsell product already exists
      const existingUpsellItem = cart.items.find(item => {
        // First check if variant ID matches
        if (item.variant_id.toString() === cartUpsellVariantId.toString()) {
          // If it has the special properties, use them for identification
          if (item.properties && item.properties._upsell === 'true' && item.properties._upsell_type === 'cart_checkbox') {
            return true;
          }
          // If no properties or no upsell properties, assume it's the upsell product
          if (!item.properties || !item.properties._upsell) {
            return true;
          }
        }
        return false;
      });

      if (existingUpsellItem) {
        return;
      }

      const upsellFormData = new FormData();
      upsellFormData.append("id", cartUpsellVariantId);
      upsellFormData.append("quantity", 1);
      // Add special properties to identify this as an upsell product
      upsellFormData.append("properties[_upsell]", "true");
      upsellFormData.append("properties[_upsell_type]", "cart_checkbox");

      const config = fetchConfig("javascript");
      config.headers["X-Requested-With"] = "XMLHttpRequest";
      delete config.headers["Content-Type"];
      config.body = upsellFormData;

      const response = await fetch(`${routes.cart_add_url}`, config);
      if (!response.ok) {
        const errorText = await response.text();
        console.error("Failed to add upsell product:", errorText);
        throw new Error("Failed to add upsell product");
      }

      this.onCartUpdate();
    } catch (error) {
      console.error("Error adding upsell product:", error);
      throw error;
    }
  }

  async removeUpsellProduct(cartUpsellVariantId) {
    try {
      // First, get the current cart to find the upsell item
      const cartResponse = await fetch(`${routes.cart_url}.js`);
      const cart = await cartResponse.json();
      
      // Find the upsell item by variant ID and properties
      const upsellItem = cart.items.find(item => {
        // First check if variant ID matches
        if (item.variant_id.toString() === cartUpsellVariantId.toString()) {
          // If it has the special properties, use them for identification
          if (item.properties && item.properties._upsell === 'true' && item.properties._upsell_type === 'cart_checkbox') {
            return true;
          }
          // If no properties or no upsell properties, assume it's the upsell product
          if (!item.properties || !item.properties._upsell) {
            return true;
          }
        }
        return false;
      });

      if (!upsellItem) {
        this.removingUpsellProduct = false;
        return;
      }

      // Use the cart update API with the item's key for precise removal
      const updateData = {
        updates: {
          [upsellItem.key]: 0
        }
      };

      const response = await fetch(`${routes.cart_update_url}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify(updateData)
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Failed to remove upsell product:", errorText);
        throw new Error("Failed to remove upsell product");
      }

      this.onCartUpdate();
      this.removingUpsellProduct = false;
      
    } catch (error) {
      console.error("Error removing upsell product:", error);
      this.removingUpsellProduct = false;
    }
  }

  getSectionsToRender() {
    const mainCartItemsEl = document.getElementById("main-cart-items");
    const mainCartFooterEl = document.getElementById("main-cart-footer");

    return [
      {
        id: "main-cart-items",
        section: mainCartItemsEl ? mainCartItemsEl.dataset.id : undefined,
        selector: ".js-contents",
      },
      {
        id: "cart-icon-bubble",
        section: "cart-icon-bubble",
        selector: ".shopify-section",
      },
      {
        id: "cart-live-region-text",
        section: "cart-live-region-text",
        selector: ".shopify-section",
      },
      {
        id: "main-cart-footer",
        section: mainCartFooterEl ? mainCartFooterEl.dataset.id : undefined,
        selector:
          mainCartFooterEl && mainCartFooterEl.dataset.b2bCartMode === "true"
            ? ".cart__blocks"
            : ".js-contents",
      },
    ];
  }

  renderCartState(parsedState, options = {}) {
    const {
      line,
      quantityElement,
      items,
      name,
      variantId,
      skipQuantityMessage = false,
    } = options;

    this.classList.toggle("is-empty", parsedState.item_count === 0);
    const cartDrawerWrapper = document.querySelector("cart-drawer");
    const cartFooter = document.getElementById("main-cart-footer");

    if (cartFooter)
      cartFooter.classList.toggle("is-empty", parsedState.item_count === 0);
    if (cartDrawerWrapper && this.tagName === "CART-DRAWER-ITEMS")
      cartDrawerWrapper.classList.toggle(
        "is-empty",
        parsedState.item_count === 0
      );

    this.getSectionsToRender().forEach((section) => {
      const sectionElement = document.getElementById(section.id);
      if (
        !sectionElement ||
        !parsedState.sections ||
        !parsedState.sections[section.section]
      ) {
        return;
      }

      const sourceDocument = new DOMParser().parseFromString(
        parsedState.sections[section.section],
        "text/html"
      );
      const sourceCartFooter = sourceDocument.querySelector(
        "#main-cart-footer"
      );
      const sourceCartDrawer = sourceDocument.querySelector("cart-drawer");
      let selector = section.selector;

      if (cartFooter && sourceCartFooter) {
        const currentB2bCartMode =
          cartFooter.dataset.b2bCartMode === "true";
        const sourceB2bCartMode =
          sourceCartFooter.dataset.b2bCartMode === "true";

        if (currentB2bCartMode !== sourceB2bCartMode) {
          selector = "#main-cart-footer";
        } else if (sourceB2bCartMode) {
          selector = ".cart__blocks";
        }
        cartFooter.dataset.b2bCartMode =
          sourceB2bCartMode ? "true" : "false";
      }

      if (cartDrawerWrapper && sourceCartDrawer) {
        cartDrawerWrapper.dataset.b2bCartMode =
          sourceCartDrawer.dataset.b2bCartMode === "true" ? "true" : "false";
      }

      const elementToReplace =
        sectionElement.querySelector(selector) || sectionElement;
      const sourceElement = sourceDocument.querySelector(selector);
      if (sourceElement) {
        elementToReplace.innerHTML = sourceElement.innerHTML;
      }
    });

    if (this.tagName !== "CART-DRAWER-ITEMS") {
      const cartDrawerItems = document.querySelector("cart-drawer-items");
      if (
        cartDrawerItems &&
        cartDrawerItems !== this &&
        typeof cartDrawerItems.onCartUpdate === "function"
      ) {
        // Full-cart section responses do not contain the drawer. Refresh it
        // directly; onCartUpdate does not publish, so this cannot feed back.
        cartDrawerItems.onCartUpdate();
      }
    }

    const updatedValue =
      line && parsedState.items[line - 1]
        ? parsedState.items[line - 1].quantity
        : undefined;
    let message = "";

    if (
      !skipQuantityMessage &&
      line &&
      quantityElement &&
      items &&
      items.length === parsedState.items.length &&
      updatedValue !== parseInt(quantityElement.value)
    ) {
      if (typeof updatedValue === "undefined") {
        message = window.cartStrings.error;
      } else {
        message = window.cartStrings.quantityError.replace(
          "[quantity]",
          updatedValue
        );
      }
    }

    if (line) this.updateLiveRegions(line, message);

    const lineItem = line ? this.getCartLineElements(line).lineItem : null;

    if (lineItem && name && lineItem.querySelector(`[name="${name}"]`)) {
      cartDrawerWrapper
        ? trapFocus(cartDrawerWrapper, lineItem.querySelector(`[name="${name}"]`))
        : lineItem.querySelector(`[name="${name}"]`).focus();
    } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
      trapFocus(
        cartDrawerWrapper.querySelector(".drawer__inner-empty"),
        cartDrawerWrapper.querySelector("a")
      );
    } else if (document.querySelector(".cart-item") && cartDrawerWrapper) {
      trapFocus(cartDrawerWrapper, document.querySelector(".cart-item__name"));
    }

    this.updateProgressBar({
      total_price: parsedState.total_price,
      items_subtotal_price: parsedState.items_subtotal_price,
    });

    publish(PUB_SUB_EVENTS.cartUpdate, {
      source: "cart-items",
      cartData: parsedState,
      variantId: variantId,
    });
    this.updateCartUpsellToggleState().catch((e) => console.error(e));
    this.updateCartUpsellVisibility(parsedState.item_count);
  }

  removeBundleGroup(bundleNonce, line, variantId) {
    this.enableLoading(line);

    window.MaisonBundleCart.removeBundleGroup(
      bundleNonce,
      this.getSectionsToRender()
    )
      .then((parsedState) => {
        this.renderCartState(parsedState, {
          line,
          variantId,
          skipQuantityMessage: true,
        });
      })
      .catch(() => {
        this.querySelectorAll(".loading__spinner").forEach((overlay) =>
          overlay.classList.add("hidden")
        );
        const errors =
          document.getElementById("cart-errors") ||
          document.getElementById("CartDrawer-CartErrors");
        errors.textContent = window.cartStrings.error;
      })
      .finally(() => {
        this.disableLoading(line);
      });
  }

  scaleBundleGroup(bundleNonce, quantity, line, variantId) {
    this.enableLoading(line);

    window.MaisonBundleCart.scaleBundleGroup(
      bundleNonce,
      quantity,
      this.getSectionsToRender()
    )
      .then((parsedState) => {
        this.renderCartState(parsedState, {
          line,
          variantId,
          skipQuantityMessage: true,
        });
      })
      .catch(() => {
        this.querySelectorAll(".loading__spinner").forEach((overlay) =>
          overlay.classList.add("hidden")
        );
        const errors =
          document.getElementById("cart-errors") ||
          document.getElementById("CartDrawer-CartErrors");
        errors.textContent = window.cartStrings.error;
      })
      .finally(() => {
        this.disableLoading(line);
      });
  }

  updateQuantity(line, quantity, name, variantId) {
    const bundleLine = this.getBundleLineData(line);

    if (bundleLine.nonce) {
      if (quantity === 0) {
        this.removeBundleGroup(bundleLine.nonce, line, variantId);
        return;
      }

      this.scaleBundleGroup(bundleLine.nonce, quantity, line, variantId);
      return;
    }

    this.enableLoading(line);

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);
        const { quantityElement } = this.getCartLineElements(line);
        const items = this.querySelectorAll(".cart-item");

        if (parsedState.errors) {
          quantityElement.value = quantityElement.getAttribute("value");
          this.updateLiveRegions(line, parsedState.errors);
          return;
        }

        this.renderCartState(parsedState, {
          line,
          quantityElement,
          items,
          name,
          variantId,
        });
      })
      .catch(() => {
        this.querySelectorAll(".loading__spinner").forEach((overlay) =>
          overlay.classList.add("hidden")
        );
        const errors =
          document.getElementById("cart-errors") ||
          document.getElementById("CartDrawer-CartErrors");
        errors.textContent = window.cartStrings.error;
      })
      .finally(() => {
        this.disableLoading(line);
      });
  }

  updateProgressBar({ total_price, items_subtotal_price }) {
    const progressWrapper = document.getElementById("cart-progress-wrapper");
    if (!progressWrapper) return;

    const cartTotalCents =
      progressWrapper.dataset.useItemsSubtotal === "true"
        ? items_subtotal_price
        : total_price;

    const currencyFormat = progressWrapper.dataset.currencyFormat;
    const thresholds = progressWrapper.dataset.thresholds
      .split(",")
      .map(Number);
    const preGoalMessages = progressWrapper.dataset.preGoalMessages.split("||");
    const postGoalMessages =
      progressWrapper.dataset.postGoalMessages.split("||");
    const goalPositions = progressWrapper.dataset.goalPositions
      .split(",")
      .map(Number);

    const totalThreshold = thresholds[thresholds.length - 1];
    const progressPercentage = Math.min(
      (cartTotalCents / totalThreshold) * 100,
      100
    );

    const progressBar = document.getElementById("cart-progress-bar");
    const goalIcons = document.querySelectorAll(".goal-icon");
    const goalMessageElement = document.querySelector(".goal-message");

    if (cartTotalCents === 0) {
      progressWrapper.style.display = "none";
      goalMessageElement.style.display = "none";
      progressBar.style.width = "0%";
    } else {
      progressWrapper.style.display = "block";
      const previousWidth = parseFloat(progressBar.style.width) || 0;
      progressBar.style.width = `${progressPercentage}%`;

      if (progressPercentage >= 100) {
        progressWrapper.classList.add("full");
      } else {
        progressWrapper.classList.remove("full");
      }

      let nextGoalIndex = -1;
      for (let i = 0; i < thresholds.length; i++) {
        if (cartTotalCents < thresholds[i]) {
          nextGoalIndex = i;
          break;
        }
      }

      goalIcons.forEach((goalIcon, index) => {
        const cartTotalDiff = cartTotalCents - thresholds[index];
        const icon = goalIcon.querySelector("img");
        const goalNumber = goalIcon.dataset.index;

        if (icon) {
          if (cartTotalDiff < 0) {
            const regularIconUrl = goalIcon.dataset.regularIcon;
            if (regularIconUrl) {
              icon.src = regularIconUrl;
              icon.srcset = `${regularIconUrl} 50w`;
              icon.alt = `Goal ${goalNumber}`;
            }
          } else {
            const reachedIconUrl = goalIcon.dataset.reachedIcon;
            if (reachedIconUrl) {
              icon.src = reachedIconUrl;
              icon.srcset = `${reachedIconUrl} 50w`;
              icon.alt = `Goal ${goalNumber} Reached`;
            }
          }
        }
      });

      goalMessageElement.style.display = "block";
      if (nextGoalIndex === -1) {
        const message = postGoalMessages[postGoalMessages.length - 1];
        goalMessageElement.innerHTML = message;
      } else {
        const remainingForGoal = thresholds[nextGoalIndex] - cartTotalCents;
        const remainingAmount = remainingForGoal / 100;
        const remainingAmountFormatted = this.formatCurrency(
          currencyFormat,
          remainingAmount
        );
        const preGoalMessageTemplate = preGoalMessages[nextGoalIndex];
        const message = preGoalMessageTemplate.replace(
          "[x]",
          remainingAmountFormatted
        );
        goalMessageElement.innerHTML = message;
      }
    }
  }

  formatCurrency(currencyFormat, amount) {
    let formattedAmount = "";
    formattedAmount = currencyFormat
      .replace("{{amount}}", amount.toFixed(2)) // Standard with two decimals
      .replace("{{amount_no_decimals}}", amount.toFixed(0)) // No decimals
      .replace(
        "{{amount_with_comma_separator}}",
        amount.toFixed(2).replace(".", ",")
      ) // Replace period with comma
      .replace(
        "{{amount_no_decimals_with_comma_separator}}",
        amount.toFixed(0).replace(".", ",")
      ) // No decimals, use comma
      .replace(
        "{{amount_with_apostrophe_separator}}",
        amount.toFixed(2).replace(".", "'")
      ) // Apostrophe separator
      .replace(
        "{{amount_no_decimals_with_space_separator}}",
        amount.toFixed(0).replace(/\\B(?=(\\d{3})+(?!\\d))/g, " ")
      ) // No decimals, space
      .replace(
        "{{amount_with_space_separator}}",
        amount
          .toFixed(2)
          .replace(/\\B(?=(\\d{3})+(?!\\d))/g, " ")
          .replace(".", ",")
      ) // Space separator
      .replace(
        "{{amount_with_period_and_space_separator}}",
        amount.toFixed(2).replace(/\\B(?=(\\d{3})+(?!\\d))/g, " ")
      ); // Period and space
    return formattedAmount;
  }

  updateLiveRegions(line, message) {
    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) ||
      document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError)
      lineItemError.querySelector(".cart-item__error-text").textContent =
        message;

    this.lineItemStatusElement.setAttribute("aria-hidden", true);

    const cartStatus =
      document.getElementById("cart-live-region-text") ||
      document.getElementById("CartDrawer-LiveRegionText");
    cartStatus.setAttribute("aria-hidden", false);

    setTimeout(() => {
      cartStatus.setAttribute("aria-hidden", true);
    }, 1000);
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector).innerHTML;
  }

  enableLoading(line) {
    const mainCartItems =
      document.getElementById("main-cart-items") ||
      document.getElementById("CartDrawer-CartItems");
    mainCartItems.classList.add("cart__items--disabled");

    const cartItemElements = this.querySelectorAll(
      `#CartItem-${line} .loading__spinner`
    );
    const cartDrawerItemElements = this.querySelectorAll(
      `#CartDrawer-Item-${line} .loading__spinner`
    );

    [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) =>
      overlay.classList.remove("hidden")
    );

    document.activeElement.blur();
    if (this.lineItemStatusElement) {
      this.lineItemStatusElement.setAttribute("aria-hidden", false);
    }
  }

  disableLoading(line) {
    const mainCartItems =
      document.getElementById("main-cart-items") ||
      document.getElementById("CartDrawer-CartItems");
    mainCartItems.classList.remove("cart__items--disabled");

    const cartItemElements = this.querySelectorAll(
      `#CartItem-${line} .loading__spinner`
    );
    const cartDrawerItemElements = this.querySelectorAll(
      `#CartDrawer-Item-${line} .loading__spinner`
    );

    cartItemElements.forEach((overlay) => overlay.classList.add("hidden"));
    cartDrawerItemElements.forEach((overlay) =>
      overlay.classList.add("hidden")
    );
  }
}

customElements.define("cart-items", CartItems);

// Auto-add hidden variant to every cart that has visible items
(function () {
  const HIDDEN_VARIANT_ID = 56912726491461;
  const ALLOWED_MARKETS = ['TEST'];

  // Window-level lock so concurrent triggers (cart:open + cartUpdate debounce)
  // and any duplicate script instances share the same guard.
  if (!window._hvSyncing) window._hvSyncing = false;

  async function syncHiddenVariant(trigger) {
    console.log('[hidden-variant] triggered by:', trigger, 'syncing already in progress:', window._hvSyncing);
    if (window._hvSyncing) return;
    window._hvSyncing = true;
    try {
      const marketAllowed = ALLOWED_MARKETS.includes(window.bamMarketCountry);
      const cart = await fetch('/cart.js').then((r) => r.json());
      const visibleItems = cart.items.filter((i) => i.variant_id !== HIDDEN_VARIANT_ID);
      const hiddenItem = cart.items.find((i) => i.variant_id === HIDDEN_VARIANT_ID);

      console.log('[hidden-variant] state:', {
        shopCountry: window.bamMarketCountry,
        marketAllowed,
        visibleItemsCount: visibleItems.length,
        hiddenItemPresent: !!hiddenItem,
        hiddenItemQty: hiddenItem ? hiddenItem.quantity : null,
      });

      if (visibleItems.length === 0 && hiddenItem) {
        // Cart contains only the hidden gift item — remove it, on any market.
        console.log('[hidden-variant] removing — cart has only the gift item');
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ id: hiddenItem.key, quantity: 0 }),
        });
        return;
      }

      if (!marketAllowed) {
        // Not an allowed market: never auto-add it, but leave any existing one alone.
        console.log('[hidden-variant] skipping — market not allowed:', window.bamMarketCountry);
        return;
      }

      if (visibleItems.length > 0 && !hiddenItem) {
        console.log('[hidden-variant] adding gift item...');
        const addResponse = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ items: [{ id: HIDDEN_VARIANT_ID, quantity: 1 }] }),
        });
        if (!addResponse.ok) {
          const errorBody = await addResponse.json().catch(() => null);
          console.error('[hidden-variant] add.js failed', addResponse.status, errorBody);
          return;
        }
        console.log('[hidden-variant] add.js succeeded');
        // Enforce qty=1 in case a concurrent add already ran and incremented it.
        await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ updates: { [String(HIDDEN_VARIANT_ID)]: 1 } }),
        });
      } else if (visibleItems.length > 0 && hiddenItem && hiddenItem.quantity > 1) {
        // Self-heal if quantity ended up > 1 for any reason.
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ id: hiddenItem.key, quantity: 1 }),
        });
      }
    } catch (e) {
      console.error('[hidden-variant] threw an exception', e);
    } finally {
      window._hvSyncing = false;
    }
  }

  console.log('[hidden-variant] script initialized, shopCountry =', window.bamMarketCountry);

  document.addEventListener('DOMContentLoaded', () => syncHiddenVariant('DOMContentLoaded'));
  document.addEventListener('cart:open', () => syncHiddenVariant('cart:open'));
  subscribe(PUB_SUB_EVENTS.cartUpdate, debounce(() => syncHiddenVariant('cartUpdate'), 600));

  // Guarantee the hidden variant is present before checkout
  document.addEventListener('click', async function (e) {
    const btn = e.target.closest('#CartDrawer-Checkout, #checkout');
    if (!btn) return;
    if (btn.dataset.hvPending === '1') {
      e.preventDefault();
      return;
    }
    if (btn.disabled || btn.dataset.hvReady === '1') return;

    e.preventDefault();
    btn.dataset.hvPending = '1';
    btn.setAttribute('aria-busy', 'true');

    try {
      await syncHiddenVariant();
    } catch (_) {}

    if (ALLOWED_MARKETS.includes(window.bamMarketCountry)) {
      try {
        const cart = await fetch('/cart.js').then((r) => r.json());
        const hasHiddenItem = cart.items.some((i) => i.variant_id === HIDDEN_VARIANT_ID);
        if (!hasHiddenItem) {
          console.warn('[hidden-variant] not present in cart before checkout', window.bamMarketCountry);
        }
      } catch (e) {
        console.error('[hidden-variant] checkout verification failed', e);
      }
    }

    if (!btn.isConnected || btn.disabled) {
      delete btn.dataset.hvPending;
      btn.removeAttribute('aria-busy');
      return;
    }

    btn.dataset.hvReady = '1';
    delete btn.dataset.hvPending;
    btn.removeAttribute('aria-busy');
    btn.click();
  }, true);
})();

if (!customElements.get("cart-note")) {
  customElements.define(
    "cart-note",
    class CartNote extends HTMLElement {
      constructor() {
        super();

        this.addEventListener(
          "input",
          debounce((event) => {
            const body = JSON.stringify({ note: event.target.value });
            fetch(`${routes.cart_update_url}`, {
              ...fetchConfig(),
              ...{ body },
            });
          }, ON_CHANGE_DEBOUNCE_TIMER)
        );
      }
    }
  );
}

(() => {
  if (window.b2bCartClearRecoveryInstalled) return;
  window.b2bCartClearRecoveryInstalled = true;

  const confirmationMessage =
    "This will permanently remove every item from your cart. Clear the cart and continue to the wholesale catalog?";
  const failureMessage =
    "We could not confirm that your cart was cleared. Please refresh and try again.";

  document.addEventListener("click", async (event) => {
    const button = event.target.closest("[data-b2b-clear-cart]");
    if (!button) return;

    event.preventDefault();
    if (button.disabled || button.dataset.b2bClearPending === "true") return;
    if (!window.confirm(confirmationMessage)) return;

    const recoveryPanel = button.closest(
      ".b2b-cart-recovery, .bundle-builder__cart-warning"
    );
    const errorElement = recoveryPanel
      ? recoveryPanel.querySelector("[data-b2b-clear-cart-error]")
      : null;
    const originalLabel = button.textContent;

    if (errorElement) {
      errorElement.hidden = true;
      errorElement.textContent = "";
    }

    button.dataset.b2bClearPending = "true";
    button.disabled = true;
    button.setAttribute("aria-busy", "true");
    button.textContent = "Clearing cart…";

    try {
      const configuredRoot =
        (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) ||
        "/";
      const root = configuredRoot.endsWith("/")
        ? configuredRoot
        : `${configuredRoot}/`;
      const response = await fetch(`${root}cart/clear.js`, {
        method: "POST",
        credentials: "same-origin",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
        body: JSON.stringify({}),
      });
      const clearedCart = await response.json().catch(() => null);

      if (
        !response.ok ||
        !clearedCart ||
        Number(clearedCart.item_count || 0) !== 0
      ) {
        throw new Error("Unable to clear B2B recovery cart");
      }

      window.location.assign(
        button.dataset.b2bClearCartRedirect || window.location.href
      );
    } catch (error) {
      console.error("B2B cart recovery failed.", error);

      if (errorElement) {
        errorElement.textContent = failureMessage;
        errorElement.hidden = false;
      } else {
        window.alert(failureMessage);
      }

      delete button.dataset.b2bClearPending;
      button.disabled = false;
      button.removeAttribute("aria-busy");
      button.textContent = originalLabel;
    }
  });
})();
